import { useEffect, useMemo, useState } from "react";
import InfoIcon from "../svg/InfoIcon";
import { CustomDropdown } from "../ui/CustomDropdown";
import { NavigationButtons } from "../ui/NavigationButtons";
import { NumberStepper } from "../ui/NumberStepper";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/indexRedux";
import { fetchPartners } from "../../store/partnerSlice";
import { PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  setHasPartner,
  setPartnerId,
  setController,
  setInverterPower,
  setOpportunityCost,
} from "../../store/pvCurtailment";

// Yuso itself cannot be the commercial partner on a PV Curtailment contract.
const YUSO_PARTNER_ID = 1;

type StepNavProps = {
  onNext?: () => void;
  onPrevious?: () => void;
  onSubmit?: () => void;
};

function AssetSpec({ onNext, onPrevious }: StepNavProps) {
  const dispatch = useDispatch();

  // read partners list
  const { partners, status, error } = useSelector((state: RootState) => state.partner);
  const accessToken = useSelector((state: RootState) => state.authv2.accessToken);

  // read persisted step2 fields from pvCurtailment slice
  const {
    hasPartner: reduxHasPartner,
    partnerId: reduxPartnerId,
    controller: reduxController,
    controllerId: reduxControllerId,
    inverterPower: reduxInverterPower,
    opportunityCost: reduxOpportunityCost,
    selectedInjection,
  } = useSelector((state: RootState) => state.pvCurtailment);

  // touched flags to avoid showing errors too early
  const [attemptedNext, setAttemptedNext] = useState(false);
  const [touched, setTouched] = useState({
    partnerChoice: false,
    partnerId: false,
    controller: false,
    inverter: false,
    opportunity: false,
  });

  // Restore touched state for fields that already have values (e.g., when navigating back).
  // attemptedNext is NOT set here — only tryNext() sets it so errors never appear before the user
  // has explicitly tried to advance. This also prevents auto-filled fields (partner, controller)
  // from surfacing validation errors on untouched fields.
  useEffect(() => {
    const hasInverterValue = reduxInverterPower !== null && reduxInverterPower !== undefined;
    const hasOpportunityValue = reduxOpportunityCost !== null && reduxOpportunityCost !== undefined;
    const hasControllerValue = !!(reduxController && String(reduxController).trim() !== "");
    const hasPartnerValue = !!(reduxPartnerId && String(reduxPartnerId).trim() !== "");
    const hasPartnerChoice = reduxHasPartner === "yes" || reduxHasPartner === "no";

    if (hasInverterValue || hasOpportunityValue || hasControllerValue || hasPartnerValue || hasPartnerChoice) {
      setTouched({
        partnerChoice: hasPartnerChoice,
        partnerId: hasPartnerValue,
        controller: hasControllerValue,
        inverter: hasInverterValue,
        opportunity: hasOpportunityValue,
      });
    }
  }, [reduxInverterPower, reduxOpportunityCost, reduxController, reduxPartnerId, reduxHasPartner]);

  // Ensure partners are loaded
  useEffect(() => {
    if (status === "idle" && partners.length === 0 && accessToken) {
      dispatch(fetchPartners({ token: accessToken }) as unknown as PayloadAction);
    }
  }, [dispatch, status, partners.length, accessToken]);

  const flexPartners = partners.filter((p) => {
    if (p.id === YUSO_PARTNER_ID) return false;
    return p.partner_revenue_bess_default != null || p.partner_profit_share_bess_default != null;
  });

  // Derive partner pre-fill data early so partnerOptions can include the bess/eboiler partner
  // even when it doesn't pass the flexPartners filter.
  const existingPartnerId = useMemo(() => {
    if (selectedInjection?.bess?.has_active_contract === 1 && selectedInjection.bess.contract_partner_id != null)
      return selectedInjection.bess.contract_partner_id;
    if (selectedInjection?.eboiler?.has_active_contract === 1 && selectedInjection.eboiler.contract_partner_id != null)
      return selectedInjection.eboiler.contract_partner_id;
    return null;
  }, [selectedInjection]);

  const existingPartnerName = useMemo(() => {
    if (selectedInjection?.bess?.has_active_contract === 1 && selectedInjection.bess.contract_partner_id != null)
      return selectedInjection.bess.contract_partner_name ?? null;
    if (selectedInjection?.eboiler?.has_active_contract === 1 && selectedInjection.eboiler.contract_partner_id != null)
      return selectedInjection.eboiler.contract_partner_name ?? null;
    return null;
  }, [selectedInjection]);

  const affectedPartnerAssets = useMemo(() => {
    const assets: string[] = [];
    if (selectedInjection?.bess?.has_active_contract === 1 && selectedInjection.bess.contract_partner_id != null)
      assets.push("BESS");
    if (selectedInjection?.eboiler?.has_active_contract === 1 && selectedInjection.eboiler.contract_partner_id != null)
      assets.push("E-Boiler");
    return assets;
  }, [selectedInjection]);

  // If the pre-filled partner from bess/eboiler is not in flexPartners, include it anyway
  // so the pre-selected value is visible and selectable in the dropdown.
  const partnerOptionsBase = [...flexPartners];
  if (
    existingPartnerId != null &&
    existingPartnerId !== YUSO_PARTNER_ID &&
    !flexPartners.some((p) => p.id === existingPartnerId)
  ) {
    const fallback = partners.find((p) => p.id === existingPartnerId);
    if (fallback) partnerOptionsBase.push(fallback);
  }

  const partnerOptions = partnerOptionsBase
    .map((p) => ({
      value: String(p.id),
      label: p.name,
      address: `${p.billingAddressStreet} ${p.billingAddressNumber}, ${p.billingAddressZip} ${p.billingAddressCity}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleNext = onNext ?? (() => {});
  const handlePrevious = onPrevious ?? (() => {});

  const [controllers, setControllers] = useState<{ id: number; name: string }[]>([]);
  const [controllersLoading, setControllersLoading] = useState(false);
  const [userChangedController, setUserChangedController] = useState(false);
  const [userChangedPartner, setUserChangedPartner] = useState(false);

  useEffect(() => {
    if (!accessToken) return;
    setControllersLoading(true);
    axios
      .get(`${import.meta.env.VITE_API_SERVICES || "http://localhost:8001"}/api/v1/sdp/controllers?product_id=9`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        setControllers(data);
      })
      .catch(() => {
        setControllers([]);
      })
      .finally(() => setControllersLoading(false));
  }, [accessToken]);

  const controllerOptions = controllers.map((c) => ({
    value: String(c.id),
    label: c.name,
  }));

  // Derive which existing active assets have a controller assigned on this site.
  // Used for both the pre-fill logic and the info banner.
  const affectedAssets = useMemo(() => {
    const assets: string[] = [];
    if (selectedInjection?.bess?.has_active_contract === 1 && selectedInjection.bess.controller_id != null)
      assets.push("BESS");
    if (selectedInjection?.eboiler?.has_active_contract === 1 && selectedInjection.eboiler.controller_id != null)
      assets.push("E-Boiler");
    return assets;
  }, [selectedInjection]);

  const existingControllerId = useMemo(() => {
    if (selectedInjection?.bess?.has_active_contract === 1 && selectedInjection.bess.controller_id != null)
      return selectedInjection.bess.controller_id;
    if (selectedInjection?.eboiler?.has_active_contract === 1 && selectedInjection.eboiler.controller_id != null)
      return selectedInjection.eboiler.controller_id;
    return null;
  }, [selectedInjection]);

  const existingControllerName = useMemo(() => {
    if (selectedInjection?.bess?.has_active_contract === 1 && selectedInjection.bess.controller_id != null)
      return selectedInjection.bess.controller_name ?? null;
    if (selectedInjection?.eboiler?.has_active_contract === 1 && selectedInjection.eboiler.controller_id != null)
      return selectedInjection.eboiler.controller_name ?? null;
    return null;
  }, [selectedInjection]);

  // True when the site has an active BESS/E-Boiler whose controller is absent from the
  // PV-certified controllers list. Only evaluated once controllers have finished loading.
  const hasUncertifiedController = useMemo(() => {
    if (controllersLoading || controllers.length === 0) return false;
    if (existingControllerId == null) return false;
    return !controllers.some((c) => c.id === existingControllerId);
  }, [controllersLoading, controllers, existingControllerId]);

  // Pre-fill the controller dropdown from an existing BESS or E-Boiler asset on the same site.
  // Guard by ID comparison (not null-check) so stale localStorage values from a previous
  // injection session are overwritten correctly.
  // Skip if the user has manually changed the selection — we must not fight their choice.
  useEffect(() => {
    if (userChangedController) return;
    if (existingControllerId == null || controllers.length === 0) return;
    if (reduxControllerId === existingControllerId) return; // already set correctly
    const match = controllers.find((c) => c.id === existingControllerId);
    if (match) {
      dispatch(setController({ name: match.name, id: match.id }));
    }
    // No match: controller not in the /controllers list — skip pre-fill, user selects manually
  }, [userChangedController, existingControllerId, reduxControllerId, controllers, dispatch]);

  // Pre-fill the partner from an existing BESS or E-Boiler contract on the same site.
  // Also auto-selects "yes" for the partner radio since we know a partner exists.
  // Exception: when the existing partner is Yuso, leave the dropdown empty so the user
  // is forced to actively pick a valid (non-Yuso) PV Curtailment partner.
  useEffect(() => {
    if (userChangedPartner) return;
    if (existingPartnerId == null) return;
    if (reduxHasPartner !== "yes") {
      dispatch(setHasPartner("yes"));
    }
    if (existingPartnerId === YUSO_PARTNER_ID) return;
    if (String(reduxPartnerId) === String(existingPartnerId)) return; // already set correctly
    dispatch(setPartnerId(String(existingPartnerId)));
  }, [userChangedPartner, existingPartnerId, reduxPartnerId, reduxHasPartner, dispatch]);

  // ---------- Inverter validation ----------
  const hasBessActive = selectedInjection?.bess?.has_active_contract === 1;

  const isInverterBelowMin =
    !hasBessActive &&
    reduxInverterPower !== null && reduxInverterPower !== undefined && Number.isFinite(reduxInverterPower)
      ? reduxInverterPower < 100
      : false;

  const isInverterAboveMax =
    reduxInverterPower !== null && reduxInverterPower !== undefined && Number.isFinite(reduxInverterPower)
      ? reduxInverterPower >= 10000
      : false;

  // ---------- Opportunity cost validation ----------
  // treat empty as missing (required). When present, validate numeric range.
  const isOpportunityInvalid =
    reduxOpportunityCost != null &&
    (!Number.isFinite(reduxOpportunityCost) || reduxOpportunityCost < 0 || reduxOpportunityCost > 450);

  // ---------- Required-field checks ----------
  const isHasPartnerMissing = reduxHasPartner !== "yes" && reduxHasPartner !== "no";
  const isPartnerMissing = reduxHasPartner === "yes" && (!reduxPartnerId || String(reduxPartnerId).trim() === "");
  const isControllerMissing = reduxControllerId == null;
  const isInverterMissing =
    reduxInverterPower === null || reduxInverterPower === undefined || Number.isNaN(Number(reduxInverterPower));
  const isOpportunityMissing = reduxOpportunityCost == null;

  // Block Next used only for programmatic validation; button remains clickable to show errors.
  // Also block if user wants to get in touch with a partner (hasPartner === "no")
  const blockNext =
    isHasPartnerMissing ||
    isPartnerMissing ||
    isControllerMissing ||
    isInverterMissing ||
    isOpportunityMissing ||
    isInverterBelowMin ||
    isOpportunityInvalid ||
    reduxHasPartner === "no";

  // ---------- Handlers that dispatch to Redux ----------
  function handleHasPartnerChange(val: "yes" | "no" | "") {
    dispatch(setHasPartner(val));
  }

  function handlePartnerSelect(val: string) {
    setUserChangedPartner(true);
    dispatch(setPartnerId(val));
    setTouched((s) => ({ ...s, partnerId: true }));
  }

  function handleControllerSelect(val: string) {
    setUserChangedController(true);
    const selected = controllers.find((c) => String(c.id) === val);
    dispatch(setController({ name: selected?.name || val, id: selected ? selected.id : null }));
    setTouched((s) => ({ ...s, controller: true }));
  }

  // inverter input: dispatch numeric (null when empty). Errors surface on blur, not while typing.
  function onChangeInverter(v: number | null) {
    dispatch(setInverterPower(v));
  }

  function onChangeOpportunity(v: number | null) {
    dispatch(setOpportunityCost(v));
    setTouched((s) => (s.opportunity ? s : { ...s, opportunity: true }));
  }

  function onBlurOpportunity() {
    setTouched((s) => ({ ...s, opportunity: true }));
  }

  // on blur for inverter to mark touched
  function onBlurInverter() {
    setTouched((s) => ({ ...s, inverter: true }));
  }

  // tryNext: mark attempted, show errors and only proceed if valid
  function tryNext() {
    setAttemptedNext(true);
    // mark all touched so errors show for all fields after first attempt
    setTouched({
      partnerChoice: true,
      partnerId: true,
      controller: true,
      inverter: true,
      opportunity: true,
    });
    if (!blockNext) {
      handleNext();
    }
  }

  const showOpportunityError = (attemptedNext || touched.opportunity) && (isOpportunityInvalid || isOpportunityMissing);

  return (
    <div>
      <div className="mb-12">
        <h1 className="text-primary-600 dark:text-white text-3xl font-semibold mb-6">Asset specifications</h1>
        <p className="text-primary-500 dark:text-gray-300 font-normal text-base text-md">
          Please provide the technical specifications of your solar PV installation. This helps us finalize your
          agreement.
        </p>
      </div>

      <div className="bg-white dark:bg-primary-900 p-12 rounded-lg w-full border-[2px] border-gray-100 dark:border-primary-800">
        {hasUncertifiedController ? (
          <>
            <div className="p-4 border-l-4 rounded-lg bg-red-50 border-red-500">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 pt-0.5">
                  <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-red-900 mb-2">
                    Controller not supported for PV Curtailment
                  </h3>
                  <p className="text-xs text-red-800">
                    The Yuso certified controller, installed for your{" "}
                    <strong>{affectedAssets.join(" and ")}</strong> steering does not support PV
                    curtailment yet. In order to proceed with curtailment, the installed controller
                    should pass our certification track for PV curtailment.{" "}
                    <a href="mailto:pvcurtailment@yuso.com" className="underline font-medium">
                      Please contact our sales team
                    </a>{" "}
                    for more information.
                  </p>
                </div>
              </div>
            </div>
            <NavigationButtons
              currentStep={2}
              totalSteps={4}
              onPrevious={handlePrevious}
              onNext={undefined}
            />
          </>
        ) : (
          <>
            <form className="flex flex-col gap-8 text-sm" onSubmit={(e) => e.preventDefault()}>
          {/* Partner selection (stylish) */}
          <div className="flex flex-col gap-3">
            <label className="font-semibold text-primary-600 dark:text-white flex items-center gap-1">
              Partner selection <span className="text-red-600">*</span>
              <div className="tooltip tooltip-primary" data-tip="Yuso works with commercial partners that support the hardware and installation to get started with Yuso PV curtailment. Contact a partner to take the next step.">
                <InfoIcon />
              </div>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Partner selection">
              <label
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer
                  ${
                    reduxHasPartner === "yes"
                      ? "bg-primary-50 dark:bg-primary-800 border-primary-600"
                      : "border-gray-200 dark:border-primary-700 hover:border-primary-200 dark:hover:border-primary-600"
                  }`}
              >
                <input
                  className="sr-only"
                  type="radio"
                  name="has-partner"
                  value="yes"
                  checked={reduxHasPartner === "yes"}
                  onChange={() => {
                    setUserChangedPartner(true);
                    handleHasPartnerChange("yes");
                    setTouched((s) => ({ ...s, partnerChoice: true }));
                  }}
                />
                <div
                  className={`flex items-center justify-center w-5 h-5 rounded-full border flex-shrink-0
                  ${reduxHasPartner === "yes" ? "bg-primary-600 border-primary-600" : "border-gray-300 dark:border-gray-600"}`}
                >
                  {reduxHasPartner === "yes" && (
                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div>
                  <div className="text-sm font-medium text-primary-600 dark:text-white">
                    I have selected my Yuso commercial partner.
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Choose this if you've already picked a partner to handle hardware and install.
                  </div>
                </div>
              </label>

              <label
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer
                  ${
                    reduxHasPartner === "no"
                      ? "bg-primary-50 dark:bg-primary-800 border-primary-600"
                      : "border-gray-200 dark:border-primary-700 hover:border-primary-200 dark:hover:border-primary-600"
                  }`}
              >
                <input
                  className="sr-only"
                  type="radio"
                  name="has-partner"
                  value="no"
                  checked={reduxHasPartner === "no"}
                  onChange={() => {
                    setUserChangedPartner(true);
                    handleHasPartnerChange("no");
                    setTouched((s) => ({ ...s, partnerChoice: true }));
                  }}
                />
                <div
                  className={`flex items-center justify-center w-5 h-5 rounded-full border flex-shrink-0
                  ${reduxHasPartner === "no" ? "bg-primary-600 border-primary-600" : "border-gray-300 dark:border-gray-600"}`}
                >
                  {reduxHasPartner === "no" && (
                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div>
                  <div className="text-sm font-medium text-primary-600 dark:text-white">
                    I want to get in touch with a Yuso commercial partner.
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Choose this if you'd like us to connect you with a partner.
                  </div>
                </div>
              </label>
            </div>

            {(attemptedNext || touched.partnerChoice) && isHasPartnerMissing && (
              <p className="text-xs text-red-600 mt-2">Please choose whether you have a partner or not.</p>
            )}

            {reduxHasPartner === "yes" && (
              <div className="mt-3">
                <label className="font-semibold text-primary-600 dark:text-white">
                  Partner name <span className="text-red-600">*</span>
                </label>
                <CustomDropdown
                  options={partnerOptions}
                  value={reduxPartnerId}
                  onChange={(v: string) => handlePartnerSelect(v)}
                  placeholder={status === "loading" ? "Loading partners..." : "Select partner"}
                  className="mt-1"
                  disabled={status === "loading" || flexPartners.length === 0}
                />
                {status === "failed" && <p className="text-xs text-red-600 mt-2">Failed to load partners: {error}</p>}
                {flexPartners.length === 0 && status !== "loading" && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">No flex partners available.</p>
                )}
                {(attemptedNext || touched.partnerId) && isPartnerMissing && (
                  <p className="text-xs text-red-600 mt-2">Please select a partner.</p>
                )}
                {affectedPartnerAssets.length > 0 &&
                  (userChangedPartner && String(reduxPartnerId) !== String(existingPartnerId) ? (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-400 dark:border-amber-500 rounded-md p-3 text-sm text-amber-800 dark:text-amber-300 mt-2">
                      <strong>Warning:</strong> Changing this partner will also update the partner for your active{" "}
                      <strong>{affectedPartnerAssets.join(" and ")}</strong> contract
                      {affectedPartnerAssets.length > 1 ? "s" : ""}
                      {existingPartnerName ? (
                        <> (currently <strong>{existingPartnerName}</strong>)</>
                      ) : (
                        ""
                      )}
                      .
                    </div>
                  ) : (
                    <div className="bg-primary-50 dark:bg-primary-800 border-l-2 border-primary-200 dark:border-primary-600 rounded-md p-3 text-sm text-primary-600 dark:text-gray-300 mt-2">
                      <strong>This partner is shared across all assets on this site.</strong> Changing it will also
                      update the partner for your active <strong>{affectedPartnerAssets.join(" and ")}</strong> contract
                      {affectedPartnerAssets.length > 1 ? "s" : ""}
                      {existingPartnerName ? (
                        <> — currently set to <strong>{existingPartnerName}</strong></>
                      ) : (
                        ""
                      )}
                      .
                    </div>
                  ))}
              </div>
            )}

            {reduxHasPartner === "no" && (
              <div className="mt-3 bg-orange-50 border-2 border-orange-400 rounded p-4">
                <p className="text-orange-800 font-medium mb-2">
                  I want to get in touch with a Yuso commercial partner.
                </p>
                <p className="text-orange-700 text-sm mb-2">
                  Choose this if you'd like us to connect you with a partner.
                </p>
                <p className="text-orange-700 text-sm mb-2">
                  Visit our{" "}
                  <a
                    href="https://yuso.com/be/nl/partners"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-600 underline font-medium"
                  >
                    partner page
                  </a>{" "}
                  to view our commercial partners.
                </p>
                <p className="text-orange-700 text-sm">
                  Need help choosing the right partner for your needs?{" "}
                  <a href="mailto:sales@yuso.com" className="text-orange-600 underline font-medium">
                    Contact our sales team
                  </a>
                  , and we'll connect you with a trusted partner.
                </p>
              </div>
            )}
          </div>

          {/* Controller dropdown */}
          <div className="flex flex-col gap-3">
            <label className="font-semibold text-primary-600 dark:text-white flex items-center gap-1">
              Yuso certified controller <span className="text-red-600">*</span>
              <div className="tooltip tooltip-primary" data-tip="The Yuso Certified Controller connects your solar installation to Yuso's platform for smart steering. Partners typically provide one or more certified controllers.">
                <InfoIcon />
              </div>
            </label>
            <CustomDropdown
              options={controllerOptions}
              value={reduxControllerId != null ? String(reduxControllerId) : ""}
              onChange={(v: string) => handleControllerSelect(v)}
              placeholder={controllersLoading ? "Loading controllers..." : "Select controller"}
              disabled={controllersLoading || controllerOptions.length === 0}
            />
            {(attemptedNext || touched.controller) && isControllerMissing && (
              <p className="text-xs text-red-600 mt-2">Please select a controller.</p>
            )}
            {affectedAssets.length > 0 ? (
              userChangedController && reduxControllerId !== existingControllerId ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-400 dark:border-amber-500 rounded-md p-3 text-sm text-amber-800 dark:text-amber-300">
                  <strong>Warning:</strong> Changing this controller will also update the controller for your active{" "}
                  <strong>{affectedAssets.join(" and ")}</strong> contract{affectedAssets.length > 1 ? "s" : ""}
                  {existingControllerName ? (
                    <> (currently <strong>{existingControllerName}</strong>)</>
                  ) : (
                    ""
                  )}
                  .
                </div>
              ) : (
                <div className="bg-primary-50 dark:bg-primary-800 border-l-2 border-primary-200 dark:border-primary-600 rounded-md p-3 text-sm text-primary-600 dark:text-gray-300">
                  <strong>This controller is shared across all assets on this site.</strong> Changing it will also update
                  the controller for your active <strong>{affectedAssets.join(" and ")}</strong> contract
                  {affectedAssets.length > 1 ? "s" : ""}
                  {existingControllerName ? (
                    <> — currently set to <strong>{existingControllerName}</strong></>
                  ) : (
                    ""
                  )}
                  .
                </div>
              )
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">Select a certified controller used on-site.</p>
            )}
          </div>

          {/* Inverter power */}
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-primary-600 dark:text-white">
              Inverter power <span className="text-red-600">*</span>
            </label>
            <NumberStepper
              value={reduxInverterPower}
              onChange={onChangeInverter}
              onBlur={onBlurInverter}
              step={10}
              min={0}
              decimals={2}
              unit="kVA"
              placeholder="Enter kVA"
              hasError={(attemptedNext || touched.inverter) && (isInverterMissing || isInverterBelowMin)}
              ariaLabel="Inverter power in kVA"
            />
            {(attemptedNext || touched.inverter) && isInverterMissing && !isInverterBelowMin && (
              <p className="text-xs text-red-600">Please enter inverter power.</p>
            )}
            {(attemptedNext || touched.inverter) && isInverterBelowMin && (
              <p className="text-xs text-red-600">
                Unfortunately, Yuso offers PV curtailment only for installations of at least 100 kVA.
              </p>
            )}
            {(attemptedNext || touched.inverter) && isInverterAboveMax && (
              <p className="text-xs text-amber-600">
                For assets with capacity ≥ 10 MVA, Yuso can provide a custom offer. Contact our{" "}
                <a href="mailto:pvcurtailment@yuso.com" className="underline hover:text-amber-700">
                  PV curtailment team
                </a>{" "}
                to discuss your needs.
              </p>
            )}
          </div>

          {/* Opportunity costs */}
          <div>
            <div className="flex items-center gap-1 mb-2">
              <h3 className="text-xl text-primary-600 dark:text-white font-semibold">Opportunity costs</h3>
              <div className="tooltip tooltip-primary" data-tip="When curtailing your local energy production, Yuso considers potential additional costs that arise from curtailed production (opportunity costs). We only curtail when the expected advantage outweighs lost revenue.">
                <InfoIcon />
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <label className="font-semibold text-primary-600 dark:text-white">
                Opportunity cost for reduced injection <span className="text-red-600">*</span>
              </label>
              <span className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                How much fixed revenue you currently get from subsidies, green certificates, ...
              </span>
              <NumberStepper
                value={reduxOpportunityCost}
                onChange={onChangeOpportunity}
                onBlur={onBlurOpportunity}
                step={1}
                min={0}
                max={450}
                decimals={2}
                unit="€/MWh"
                placeholder="e.g. 45,00"
                hasError={showOpportunityError}
                ariaLabel="Opportunity cost in euro per MWh"
              />

              {(attemptedNext || touched.opportunity) && isOpportunityInvalid ? (
                <p className="text-xs text-red-600 mt-2">Enter a valid number between 0 and 450.</p>
              ) : (attemptedNext || touched.opportunity) && isOpportunityMissing ? (
                <p className="text-xs text-red-600 mt-2">Please enter an opportunity cost.</p>
              ) : (
                <p className="text-xs text-gray-500 mt-2"></p>
              )}
            </div>
          </div>
        </form>

            <NavigationButtons
              currentStep={2}
              totalSteps={4}
              onPrevious={handlePrevious}
              onNext={reduxHasPartner === "no" ? undefined : tryNext}
              nextDisabled={blockNext}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default AssetSpec;
